import React from 'react';
import { ABIDJAN_QUARTIERS, ABIDJAN_COMMUNES } from '../utils/abidjanData';

/**
 * RecipientFormSection - Unified recipient form component
 * 
 * Handles both read-only display and edit modes for recipient information
 * with fields: name, phone, city, commune (optional), neighborhood (optional),
 * address, address_complement (used as free-text location field)
 * 
 * Pays (country) is hidden and automatically set to "Côte d'Ivoire"
 * 
 * @param {Object} recipientData - Current recipient data
 * @param {Function} onFieldChange - Callback when field changes: (field, value) => void
 * @param {Object} errors - Validation errors object
 * @param {boolean} isEditing - Whether in edit mode
 * @param {boolean} isLoading - Whether save operation is in progress
 * @param {Function} onSave - Callback when save button clicked
 * @param {Function} onCancel - Callback when cancel button clicked
 * @param {Function} onEdit - Callback when edit button clicked
 */
export default function RecipientFormSection({ 
  recipientData, 
  onFieldChange, 
  errors, 
  isEditing, 
  isLoading, 
  onSave, 
  onCancel, 
  onEdit 
}) {
  const handleInputChange = (field) => (e) => {
    onFieldChange?.(field, e?.target?.value);
  };

  const handleCommuneChange = (e) => {
    const newCommune = e?.target?.value;
    onFieldChange?.('commune', newCommune);
    // Reset neighborhood when commune changes
    onFieldChange?.('neighborhood', '');
  };

  const displayValue = (val) => (val && val?.trim() !== '') ? val : null;

  const selectedCommune = recipientData?.commune || '';
  const availableQuartiers = selectedCommune ? (ABIDJAN_QUARTIERS?.[selectedCommune] || []) : [];
  const isAutreCommune = selectedCommune === 'Autre / localité non répertoriée';

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Informations Destinataire</h2>
        </div>
        {!isEditing && onEdit && (
          <button
            onClick={onEdit}
            className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
          >
            Modifier
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        {/* Nom complet — OBLIGATOIRE */}
        <div className="col-span-1 sm:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Nom complet <span className="text-red-500">*</span>
          </label>
          {isEditing ? (
            <input
              type="text"
              value={recipientData?.name || ''}
              onChange={handleInputChange('name')}
              className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm sm:text-base min-h-[44px] text-gray-900 placeholder-gray-500 bg-white"
              placeholder="Nom complet du destinataire"
            />
          ) : (
            <p className="text-sm text-gray-900 py-2">{displayValue(recipientData?.name) || <span className="text-gray-400">—</span>}</p>
          )}
          {errors?.name && <p className="text-xs sm:text-sm text-red-600 mt-1">{errors?.name}</p>}
        </div>

        {/* Téléphone — OBLIGATOIRE */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Téléphone <span className="text-red-500">*</span>
          </label>
          {isEditing ? (
            <input
              type="tel"
              value={recipientData?.phone || ''}
              onChange={handleInputChange('phone')}
              className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm sm:text-base min-h-[44px] text-gray-900 placeholder-gray-500 bg-white"
              placeholder="+225 07 12 34 56 78"
            />
          ) : (
            <p className="text-sm text-gray-900 py-2">{displayValue(recipientData?.phone) || <span className="text-gray-400">—</span>}</p>
          )}
          {errors?.phone && <p className="text-xs sm:text-sm text-red-600 mt-1">{errors?.phone}</p>}
        </div>

        {/* Ville — facultatif, prérempli Abidjan */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Ville
          </label>
          {isEditing ? (
            <input
              type="text"
              value={recipientData?.city || ''}
              onChange={handleInputChange('city')}
              className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm sm:text-base min-h-[44px] text-gray-900 placeholder-gray-500 bg-white"
              placeholder="Abidjan"
            />
          ) : (
            <p className="text-sm text-gray-900 py-2">{displayValue(recipientData?.city) || <span className="text-gray-400">—</span>}</p>
          )}
          {errors?.city && <p className="text-xs sm:text-sm text-red-600 mt-1">{errors?.city}</p>}
        </div>

        {/* Pays — MASQUÉ, automatiquement "Côte d'Ivoire" */}
        {/* Le pays est toujours Côte d'Ivoire — champ masqué, valeur enregistrée automatiquement */}
        {!isEditing && displayValue(recipientData?.country) && (
          <div className="hidden">
            <span>{recipientData?.country}</span>
          </div>
        )}

        {/* Commune — FACULTATIF */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Commune <span className="text-xs text-gray-400 font-normal">(facultatif)</span>
          </label>
          {isEditing ? (
            <select
              value={recipientData?.commune || ''}
              onChange={handleCommuneChange}
              className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm sm:text-base min-h-[44px] text-gray-900 bg-white"
            >
              <option value="">— Non renseignée —</option>
              {ABIDJAN_COMMUNES?.map(commune => (
                <option key={commune} value={commune}>{commune}</option>
              ))}
            </select>
          ) : (
            <p className="text-sm text-gray-900 py-2">{displayValue(recipientData?.commune) || <span className="text-gray-400">—</span>}</p>
          )}
          {errors?.commune && <p className="text-xs sm:text-sm text-red-600 mt-1">{errors?.commune}</p>}
        </div>

        {/* Quartier — FACULTATIF */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Quartier <span className="text-xs text-gray-400 font-normal">(facultatif)</span>
          </label>
          {isEditing ? (
            isAutreCommune || availableQuartiers?.length === 0 ? (
              <input
                type="text"
                value={recipientData?.neighborhood || ''}
                onChange={handleInputChange('neighborhood')}
                className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm sm:text-base min-h-[44px] text-gray-900 placeholder-gray-500 bg-white"
                placeholder="Saisir le quartier librement"
              />
            ) : (
              <select
                value={recipientData?.neighborhood || ''}
                onChange={handleInputChange('neighborhood')}
                className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm sm:text-base min-h-[44px] text-gray-900 bg-white"
              >
                <option value="">— Non renseigné —</option>
                {availableQuartiers?.map(quartier => (
                  <option key={quartier} value={quartier}>{quartier}</option>
                ))}
                <option value="__autre__">Autre quartier (saisie libre)</option>
              </select>
            )
          ) : (
            <p className="text-sm text-gray-900 py-2">{displayValue(recipientData?.neighborhood) || <span className="text-gray-400">—</span>}</p>
          )}
          {errors?.neighborhood && <p className="text-xs sm:text-sm text-red-600 mt-1">{errors?.neighborhood}</p>}
        </div>

        {/* Champ libre de localisation — utilise receiver_address_complement */}
        <div className="col-span-1 sm:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Localité / quartier / repère libre <span className="text-xs text-gray-400 font-normal">(facultatif)</span>
          </label>
          {isEditing ? (
            <input
              type="text"
              value={recipientData?.address_complement || ''}
              onChange={handleInputChange('address_complement')}
              className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm sm:text-base min-h-[44px] text-gray-900 placeholder-gray-500 bg-white"
              placeholder="Ex : Bingerville Centre, Carrefour Y après le marché, Zone non encore référencée…"
            />
          ) : (
            <p className="text-sm text-gray-900 py-2">{displayValue(recipientData?.address_complement) || <span className="text-gray-400">—</span>}</p>
          )}
        </div>

        {/* Adresse — champ libre */}
        <div className="col-span-1 sm:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Adresse
          </label>
          {isEditing ? (
            <textarea
              value={recipientData?.address || ''}
              onChange={handleInputChange('address')}
              rows={3}
              className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm sm:text-base text-gray-900 placeholder-gray-500 bg-white"
              placeholder="Rue, repère, immeuble, indication locale, description d'accès…"
            />
          ) : (
            <p className="text-sm text-gray-900 py-2">{displayValue(recipientData?.address) || <span className="text-gray-400">—</span>}</p>
          )}
          {errors?.address && <p className="text-xs sm:text-sm text-red-600 mt-1">{errors?.address}</p>}
        </div>
      </div>

      {isEditing && (onSave || onCancel) && (
        <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
          {onCancel && (
            <button
              onClick={onCancel}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Annuler
            </button>
          )}
          {onSave && (
            <button
              onClick={onSave}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>Enregistrement...</span>
                </>
              ) : (
                <span>Enregistrer</span>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
}